import {
  Fragment,
  useState,
  useEffect,
  createContext,
  useCallback,
  useRef,
  useContext,
} from "react";
// import { AppContext } from "./app.context";
import { UserType } from "@/types/userType";
import Cookies from "js-cookie";
import { StoreApi, createStore, useStore } from "zustand";
//component
import Notification from "@/components/common/notification/notification";
import { NotificationType } from "@/components/common/notification/types";
import ScreenWidth from "@/components/common/ScreenWidth";
//styles
import styles from "../../routes/login/login.module.scss";
import classNames from "classnames";
//api
import { getUser } from "@/apis/getUser";
import { getMapMyToken } from "@/apis/getMapMyToken";
import { GameType } from "@/routes/game/type";
import LoginModal from "@/components/common/LoginModal";
import { mixpanelLogOut } from "@/helper/mixpanelService";
import Toast from "@/components/common/toast/toast";
import { ToastPropsType } from "@/components/common/toast/types";
import { useRouter } from "next/router";
import { VenueData } from "@/routes/venue/types";
import { userApi } from "@/services/api";
import { VenueStoreProvider } from "@/stores/venueStore";
import { useJuspayStore } from "@/stores/juspayStore";
import LoginProvider, {
  loginStore,
  useLoginStore,
} from "../login/login.provider";
import { LoginStep } from "@/components/common/LoginModal/types";
import { AnimationType } from "@/components/common/LoginModal/loginmodal";
import useViewportWidth from "@/hooks/useViewportWidth";
type Props = {
  children: React.ReactNode;
};

type AppStore = {
  user: UserType | undefined;
  appConfig: any | undefined;
  askToJoinGame: boolean;
  mapMyToken: {
    token: string | undefined;
    setTime: string | undefined;
  };
  gameData: GameType | undefined;
  venueData: VenueData | undefined;
  isMobileView: boolean;
  notification: {
    message: string;
    type: NotificationType;
    show: boolean;
  };
  storeRouter: {
    lastRoute: string;
    currentRoute: string;
  };
  loading: boolean;
  setLoading: (status: boolean) => void;
  setAppState: (updates: Partial<AppStore>) => void;
  getUserData: (token: string) => Promise<void>;
  showNotification: (message: string, type: NotificationType) => void;
  setLoginModalVisibility: (status: boolean) => void;
  setToastVisibility: (props: ToastPropsType) => void;
  fetchAppConfig: () => Promise<void>;
  logout: () => void;
};

const AppContext = createContext<StoreApi<AppStore> | undefined>(undefined);

const AppProvider = ({ children }: Props): JSX.Element => {
  const loginModalRef = useRef<null | {
    setVisibility: (status: boolean) => void;
  }>();
  const router = useRouter();
  const toastRef = useRef<null | {
    setVisibility: (props: ToastPropsType) => void;
  }>();

  const initializeJuspay = useJuspayStore((state) => state.initializeJuspay);
  const {
    setLoginStep,
    setState: setLoginStoreState,
    resetLoginStore,
  } = loginStore.getState();
  const viewportWidth = useViewportWidth();

  const putScript = useCallback((token: string) => {
    const removescript = document.getElementById("map-my-script");
    if (removescript) {
      removescript.remove();
    }

    const script = document.createElement("script");
    script.id = "map-my-script";
    script.src = `https://apis.mappls.com/advancedmaps/api/${token}/map_sdk_plugins?v=3.0&libraries=search`;
    script.async = true;
    document.body.appendChild(script);
  }, []);

  const fetchToken = useCallback(async () => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      putScript(token);
    }
    const tokenExpiry = localStorage.getItem("expires_in");
    const expiryTimestamp = new Date(Number(tokenExpiry));
    const currentTimestamp = new Date();

    if (currentTimestamp >= expiryTimestamp) {
      try {
        const res = await getMapMyToken();
        if (res?.access_token) {
          localStorage.setItem("accessToken", res?.access_token);
          const tokenExpiry = Date.now() + res?.expires_in * 1000;
          localStorage.setItem("expires_in", String(tokenExpiry));
          if (res?.access_token) {
            putScript(res?.access_token);
          }
        }
      } catch (e) {
        console.error("error fetching token");
      }
    }
  }, [putScript]);

  useEffect(() => {
    fetchToken();

    return () => {
      const script = document.getElementById("map-my-script");
      if (script) {
        script.remove();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [store] = useState(() =>
    createStore<AppStore>((set, get) => ({
      user: undefined,
      appConfig: undefined,
      askToJoinGame: false,
      mapMyToken: { token: undefined, setTime: undefined },
      gameData: undefined,
      venueData: undefined,
      isMobileView: false,
      storeRouter: { lastRoute: "", currentRoute: "" },
      notification: { message: "", type: NotificationType.NORMAL, show: false },
      loading: false,
      setLoading: (status) => {
        set((state) => ({ ...state, loading: status }));
      },
      setAppState: (updates) => set((state) => ({ ...state, ...updates })),
      getUserData: async (token) => {
        try {
          const data = await getUser(token);
          // initializeJuspay(data)
          set({ user: data });
        } catch (e) {
          console.error("getUser error", e);
        }
      },
      showNotification: (message, type) => {
        set({ notification: { message, type, show: true } });
      },
      setLoginModalVisibility: (status) => {
        const user = get().user;

        if (loginModalRef?.current) {
          loginModalRef?.current?.setVisibility(status);
        }
        if (!user) {
          setLoginStep(LoginStep.PHONE);
        }
        if (user && !user.name) {
          setLoginStoreState({
            animationType: AnimationType.SLIDEINRIGHT,
            token: Cookies.get("ACCESS_TOKEN") || "",
            id: user?._id || "",
            name: user?.name || "",
            step: viewportWidth > 430 ? LoginStep.NAME_EMAIL : LoginStep.NAME,
            // step: LoginStep.NAME,
            showBackButton: false,
          });
          return;
        }
        // if (user && !user.email) {
        //   setLoginStoreState({
        //     animationType: AnimationType.SLIDEINRIGHT,
        //     token: Cookies.get("ACCESS_TOKEN") || "",
        //     id: user?._id || "",
        //     name: user?.name || "",
        //     phone: user?.phone || "",
        //     step: LoginStep.EMAIL,
        //     showBackButton: false,
        //   });
        //   return;
        // }
        if ((user && !user.handle) || user?.handle === "new_user") {
          setLoginStoreState({
            animationType: AnimationType.SLIDEINRIGHT,
            token: Cookies.get("ACCESS_TOKEN") || "",
            id: user?._id || "",
            name: user?.name || "",
            email: user?.email || "",
            phone: user?.phone || "",
            step: LoginStep.HANDLE,
            showBackButton: false,
          });
          return;
        }
      },
      setToastVisibility: (props) => {
        if (toastRef?.current) {
          toastRef?.current?.setVisibility(props);
        }
      },
      fetchAppConfig: async () => {
        try {
          set({ loading: true });
          const appConfigResponse = await userApi.getAppConfig();
          set({ appConfig: appConfigResponse.data, loading: false });
        } catch (e) {
          console.error("getAppConfig error", e);
        }
      },
      logout: () => {
        const user = store.getState().user;
        resetLoginStore();
        mixpanelLogOut({
          _id: user?._id,
          email: user?.email,
          name: user?.name,
          phone: user?.phone,
        });

        set({ user: undefined });
        Cookies.remove("LAST_ROUTE");
        Cookies.remove("ACCESS_TOKEN");
        Cookies.remove("_ID");
      },
    }))
  );

  useEffect(() => {
    const unsubscribe = store.subscribe((state) => {
      console.log("State updated:", state);
    });

    return () => unsubscribe(); // Cleanup subscription when component unmounts
  }, [store]);

  useEffect(() => {
    store.setState((state) => ({
      ...state,
      storeRouter: {
        lastRoute: state.storeRouter.currentRoute,
        currentRoute: router.asPath,
      },
    }));
  }, [router]);

  return (
    <div className={classNames(styles["App1"], styles["p-0"])}>
      <AppContext.Provider value={store}>
        <LoginProvider>
          <VenueStoreProvider>
            <ScreenWidth />
            <Notification />
            <Fragment>{children}</Fragment>
            <LoginModal ref={loginModalRef} />
            <Toast ref={toastRef} />
          </VenueStoreProvider>
        </LoginProvider>
      </AppContext.Provider>
    </div>
  );
};

export default AppProvider;
export function useAppStore<T>(selector: (state: AppStore) => T) {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("AppProvider is missing");
  }
  return useStore(context, selector);
}
